
import { Component, Mixins } from "vue-property-decorator";
import { uiStore, documentStore } from "@/store/store";
import { DragMixin } from "./DragMixin";
import { ValidMouseDragInfo } from "@/lib/canvas/drag/drag";
import {
  getOnCanvasColumnWidthInPercent,
  calcNewPercentWidthForColumn,
  getOnCanvasColumnWidthInPixels
} from "@/lib/columns/canvas";
import { resizeTwoColumnsAndCommit } from "@/lib/columns/drag";
import { getColumnsElement, isColumnElement } from "@/lib/columns/helpers";
import { NewColumnWidths } from "@/lib/columns/drag";
import { ElementPositionAndSize } from "@/lib/canvas/helpers";
import { breakdanceUndo } from "@/lib/undo/undo";
import { BASE_BREAKPOINT_ID } from "@/lib/breakpoints/breakpoints";
import { getNodeIndex, getNodeParent } from "@/lib/tree/tree";
import lodash from "lodash";

@Component({})
export default class ColumnDragSizer extends Mixins(DragMixin) {
  resizedWidths:
    | false
    | { left: NewColumnWidths }
    | { right: NewColumnWidths } = false;

  widthOnDragStart: number | false = false;

  get columnsAreNotResizeableAtBaseBreakpoint() {
    if (!this.activeColumn) return false;

    const columns = getColumnsElement(this.activeColumn);
    if (!columns) return false;

    const VERTICAL_STACKING_PATH = "design.layout.stack_vertically";
    const FIFTY_FIFTY_PATH = "design.layout.fifty_percent";

    const verticalStacking = lodash.get(
      columns.data.properties,
      VERTICAL_STACKING_PATH,
      false
    );
    const fiftyFifty = lodash.get(
      columns.data.properties,
      FIFTY_FIFTY_PATH,
      false
    );

    return (
      verticalStacking === BASE_BREAKPOINT_ID ||
      fiftyFifty === BASE_BREAKPOINT_ID
    );
  }

  get shouldShow() {
    return (
      uiStore.currentBreakpoint.id === BASE_BREAKPOINT_ID &&
      this.activeColumn &&
      !this.columnsAreNotResizeableAtBaseBreakpoint
    );
  }

  dragStartCallback() {
    breakdanceUndo.startTransaction();
  }

  dragCallback(
    _elementPositionAndSize: ElementPositionAndSize,
    mouseDragInfo: ValidMouseDragInfo,
    handle: string
  ) {
    if (!this.activeColumn || (handle !== "left" && handle !== "right")) return;

    const columnIndex = getNodeIndex(
      documentStore.document.tree,
      this.activeColumn.id
    );
    const columnParent = getNodeParent(
      documentStore.document.tree,
      this.activeColumn.id
    );

    if (columnIndex === false || columnParent === false) return;

    let adjustPx = 0;

    if (!this.widthOnDragStart) {
      this.widthOnDragStart = getOnCanvasColumnWidthInPixels(this.activeColumn);
    }

    if (!this.widthOnDragStart) return;

    adjustPx =
      handle === "right"
        ? mouseDragInfo.totalXChange
        : -1 * mouseDragInfo.totalXChange;

    /* we need to product mouseDragInfo from having any NaN values */

    const newWidth = calcNewPercentWidthForColumn(
      this.activeColumn,
      adjustPx,
      this.widthOnDragStart
    );

    if (!newWidth) return;

    const resizedWidths = resizeTwoColumnsAndCommit(
      columnParent,
      columnIndex,
      newWidth,
      handle
    );

    if (resizedWidths) {
      // @ts-ignore
      this.resizedWidths = {
        [handle]: {
          resizedWidth: Number(resizedWidths.resizedWidth.toFixed(2)),
          siblingWidth: Number(resizedWidths.siblingWidth.toFixed(2))
        }
      };
    }
  }

  dragEndCallback() {
    this.resizedWidths = false;
    this.widthOnDragStart = false;
    breakdanceUndo.endTransaction("Resizing column");
  }

  get isNotFarLeftColumn() {
    if (!this.activeColumn) return false;

    const columnIndex = getNodeIndex(
      documentStore.document.tree,
      this.activeColumn.id
    );
    if (columnIndex === false) return false;

    return columnIndex !== 0;
  }

  get isNotFarRightColumn() {
    if (!this.activeColumn) return false;

    const columnIndex = getNodeIndex(
      documentStore.document.tree,
      this.activeColumn.id
    );
    const columnParent = getNodeParent(
      documentStore.document.tree,
      this.activeColumn.id
    );
    if (columnIndex === false || columnParent === false) return false;

    return columnIndex !== columnParent.children.length - 1;
  }

  get activeColumn() {
    return isColumnElement(uiStore.activeElement)
      ? uiStore.activeElement
      : false;
  }

  get columnWidthInPercent() {
    if (!this.activeColumn) return false;
    return getOnCanvasColumnWidthInPercent(this.activeColumn);
  }
}
